import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useDocumentData } from "react-firebase-hooks/firestore";
import SummernoteEditor from "../components/SummernoteEditor";
import { firebaseApp } from "../config/firebase";
import { Loader } from "../hooks/useLoading";
import { toastMessage } from "../utils/toastMessage";

const db = getFirestore(firebaseApp);

const EditEmailTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [templateRef, loading, error] = useDocumentData(
    doc(db, "email_templates", id),
  );

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [initialized, setInitialized] = useState(false); // gate editor mount until body is ready
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (templateRef) {
      setName(id);
      setSubject(templateRef.subject || "");
      setBody(templateRef.body || "");
      setInitialized(true);
    }
  }, [templateRef, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!subject.trim()) {
      newErrors.subject = "Email Subject is required.";
    }

    if (!body.trim()) {
      newErrors.body = "Email Body is required.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setSaving(true);
      const templateDocRef = doc(db, "email_templates", id);

      await updateDoc(templateDocRef, {
        subject: subject.trim(),
        body: `<body style="margin: 0 auto;width: 100vw;max-width: 800px;font-size: 20px;text-align: center;">`+body+`</body>`,
        updated_at: serverTimestamp(),
      });

      toastMessage("Email template updated successfully.", "success");
      navigate("/dashboard/email-templates");
    } catch (err) {
      console.error("Email template update error:", err);
      toastMessage("Failed to update email template.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    console.error("Error fetching email template:", error);
    return <p className="text-danger">Error loading template.</p>;
  }

  const formatTemplateName = (name) => {
    if (!name) return "";
    const formatted = name.replace(/_/g, " ");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  return (
    <>
      {(loading || saving) && <Loader />}
      <header className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h4 className="mb-1">Edit Email Template</h4>
          <small className="text-muted">
            Editing template: <strong>{formatTemplateName(name)}</strong>
          </small>
        </div>
        <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>
          Back to List
        </Button>
      </header>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>
            Template Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={formatTemplateName(name)}
            placeholder="Enter template name"
            readOnly
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Email Subject <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject"
            disabled={saving}
            isInvalid={!!errors.subject}
          />
          <Form.Control.Feedback type="invalid">
            {errors.subject}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Email Body <span className="text-danger">*</span>
          </Form.Label>
          {initialized && (
            <SummernoteEditor
              value={body}
              onChange={setBody}
              disabled={saving}
            />
          )}
          <Form.Control.Feedback type="invalid" className={errors.body ? "d-block" : ""}>
            {errors.body}
          </Form.Control.Feedback>
        </Form.Group>

        <div className="d-flex justify-content-end">
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default EditEmailTemplate;